import { useState, useMemo, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { useSearchParams } from "react-router-dom";
import { faSearch, faTable, faThLarge } from "@fortawesome/free-solid-svg-icons";
import Icon from "../../common/elements/Icon";
import StateChip from "./StateChip";
import { getProjectName, PROJECT_MAP } from "../utils/thorProjects";
import { EXPERIMENT_STATE_ORDER, getStateLabel } from "../utils/thorState";

const StatusDot = ({ status }) => (
    <span
        title={status ?? 'unknown'}
        style={{
            display: 'inline-block',
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            backgroundColor: status === 'running' ? '#48c78e' : '#f14668',
        }}
    />
);

const fmtDate = (dt) => {
    if (!dt) return '—';
    return new Date(dt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const matches = (val, query) => val != null && String(val).toLowerCase().includes(query);

// States where the experiment owner likely has pending action
const OWNER_ACTION_STATES = new Set(['design', 'design_review', 'pre_prod_validation', 'ended']);

// Kanban column config — which states to show as columns, and in what order
const KANBAN_COLUMNS = [
    { key: 'design',              label: 'Design',              color: '#3298dc' },
    { key: 'design_review',       label: 'Design Review',       color: '#ffdd57' },
    { key: 'instrumentation',     label: 'Instrumentation',     color: '#f4a72a' },
    { key: 'pre_prod_validation', label: 'Pre-prod Validation', color: '#3273dc' },
    { key: 'running',             label: 'Running',             color: '#48c78e' },
    { key: 'paused',              label: 'Paused',              color: '#ffb347' },
    { key: 'ended',               label: 'Test Ending',         color: '#363636' },
    { key: 'cleanup',             label: 'Clean Up',            color: '#888' },
    { key: 'finished',            label: 'Finished',            color: '#257942' },
];

// State filter options shown in the dropdown
const STATE_FILTER_OPTIONS = [
    { value: '',                  label: 'All states' },
    { value: 'design',            label: 'Design' },
    { value: 'design_review',     label: 'Design Review' },
    { value: 'instrumentation',   label: 'Instrumentation' },
    { value: 'pre_prod_validation', label: 'Pre-prod Validation' },
    { value: 'running',           label: 'Running' },
    { value: 'paused',            label: 'Paused' },
    { value: 'ended',             label: 'Test Ending & Decision' },
    { value: 'cleanup',           label: 'Clean Up' },
    { value: 'finished',          label: 'Finished' },
    { value: 'cancelled',         label: 'Cancelled' },
];

const KanbanCard = ({ exp, onSelect }) => (
    <div
        role="button"
        tabIndex={0}
        onClick={() => onSelect(exp)}
        onKeyDown={e => e.key === 'Enter' && onSelect(exp)}
        style={{
            background: '#fff',
            border: '1px solid #dbdbdb',
            borderRadius: '5px',
            padding: '0.45rem 0.55rem',
            marginBottom: '0.4rem',
            cursor: 'pointer',
            fontSize: '0.8rem',
        }}
        className="kanban-card"
    >
        <p className="has-text-weight-semibold" style={{ marginBottom: '0.2rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {exp.name ?? exp.key}
        </p>
        <p className="has-text-grey" style={{ fontSize: '0.75rem' }}>{getProjectName(exp.projectId)}</p>
        <div style={{ display: 'flex', gap: '0.35rem', marginTop: '0.3rem', alignItems: 'center' }}>
            <span style={{ fontSize: '0.7rem', color: '#888' }}>{exp.owner ?? '—'}</span>
            <span style={{ marginLeft: 'auto', display: 'flex', gap: '3px', alignItems: 'center' }}>
                <StatusDot status={exp.devStatus} />
                <StatusDot status={exp.prodStatus} />
            </span>
        </div>
    </div>
);

const KanbanBoard = ({ experiments, onSelect }) => {
    const byState = useMemo(() => {
        const map = {};
        KANBAN_COLUMNS.forEach(c => { map[c.key] = []; });
        experiments.forEach(exp => {
            if (map[exp.state]) map[exp.state].push(exp);
        });
        return map;
    }, [experiments]);

    const nonEmptyColumns = KANBAN_COLUMNS.filter(c => byState[c.key]?.length > 0);

    if (nonEmptyColumns.length === 0) {
        return <p className="has-text-grey has-text-centered py-4">No experiments match your filter.</p>;
    }

    return (
        <div style={{
            overflowX: 'auto',
            height: 'calc(100vh - 340px)',
            minHeight: '300px',
        }}>
            <div style={{ display: 'flex', gap: '0.6rem', height: '100%', alignItems: 'stretch' }}>
                {nonEmptyColumns.map(col => (
                    <div key={col.key} style={{ flex: '0 0 180px', minWidth: '180px', display: 'flex', flexDirection: 'column' }}>
                        {/* Column header */}
                        <div style={{
                            borderTop: `3px solid ${col.color}`,
                            background: '#f8f8f8',
                            borderRadius: '0 0 4px 4px',
                            padding: '0.4rem 0.5rem 0.3rem',
                            marginBottom: '0.4rem',
                            flexShrink: 0,
                        }}>
                            <span style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#555' }}>
                                {col.label}
                            </span>
                            <span className="tag is-light is-small ml-2" style={{ fontSize: '0.68rem' }}>
                                {byState[col.key].length}
                            </span>
                        </div>
                        {/* Scrollable cards area */}
                        <div style={{ overflowY: 'auto', flexGrow: 1, paddingRight: '2px' }}>
                            {byState[col.key].map(exp => (
                                <KanbanCard key={exp.key} exp={exp} onSelect={onSelect} />
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const BANNER_SESSION_KEY = 'thor_attention_banner_dismissed';

const AttentionBanner = ({ experiments, onSelect, userEmail }) => {
    const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(BANNER_SESSION_KEY) === '1');

    if (!userEmail) return null;

    const myExps = experiments.filter(exp =>
        (exp.email?.toLowerCase() === userEmail.toLowerCase() || exp.owner?.toLowerCase() === userEmail.toLowerCase()) &&
        OWNER_ACTION_STATES.has(exp.state)
    );

    if (myExps.length === 0) return null;

    const dismiss = () => { sessionStorage.setItem(BANNER_SESSION_KEY, '1'); setDismissed(true); };
    const reshow  = () => { sessionStorage.removeItem(BANNER_SESSION_KEY);   setDismissed(false); };

    const ACTION_HINT = {
        design: '→ Complete setup and request Design Review',
        design_review: '→ Both Design Reviews (Analyst + Engineer) must be approved',
        pre_prod_validation: '→ Owner Pre-prod Signoff needed',
        ended: '→ Fill in Outcome to proceed to Clean Up',
    };

    if (dismissed) {
        return (
            <div className="mb-3" style={{ fontSize: '0.78rem' }}>
                <button type="button" className="button is-ghost is-small has-text-info" style={{ padding: '0 0.25rem' }} onClick={reshow}>
                    ⚡ {myExps.length} experiment{myExps.length !== 1 ? 's' : ''} need{myExps.length === 1 ? 's' : ''} your attention — show
                </button>
            </div>
        );
    }

    return (
        <div className="notification is-info is-light mb-4" style={{ position: 'relative', padding: '0.85rem 1rem' }}>
            <button
                className="delete is-small"
                style={{ position: 'absolute', top: '0.5rem', right: '0.5rem' }}
                onClick={dismiss}
                title="Dismiss"
            />
            <p className="has-text-weight-semibold mb-2" style={{ fontSize: '0.85rem' }}>
                ⚡ {myExps.length} experiment{myExps.length !== 1 ? 's' : ''} need{myExps.length === 1 ? 's' : ''} your attention
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                {myExps.map(exp => (
                    <div key={exp.key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <button
                            type="button"
                            className="button is-ghost is-small"
                            style={{ padding: 0, fontWeight: 600, fontSize: '0.82rem', height: 'auto', color: '#1d72aa', textDecoration: 'underline' }}
                            onClick={() => onSelect(exp)}
                        >
                            {exp.name ?? exp.key}
                        </button>
                        <StateChip state={exp.state} />
                        <span className="has-text-grey" style={{ fontSize: '0.78rem' }}>
                            {ACTION_HINT[exp.state]}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

const ExperimentSearch = ({ experiments, onSelect }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [query, setQuery] = useState('');
    const [projectFilter, setProjectFilter] = useState('');
    const [stateFilter, setStateFilter] = useState('');
    const [mineOnly, setMineOnly] = useState(() => {
        if (searchParams.get('mine') === '1') return true;
        return sessionStorage.getItem('thor_mine_only') === '1';
    });
    const [viewMode, setViewMode] = useState(() => {
        return searchParams.get('view') === 'board' ? 'board' : 'list';
    });
    const { instance } = useMsal();
    const userEmail = instance.getAllAccounts()[0]?.username ?? '';

    // Sync mineOnly → URL + sessionStorage
    useEffect(() => {
        setSearchParams(prev => {
            const p = new URLSearchParams(prev);
            // Only update search-view params when no experiment is selected
            if (p.has('experiment')) return p;
            if (mineOnly) p.set('mine', '1'); else p.delete('mine');
            return p;
        }, { replace: true });
        sessionStorage.setItem('thor_mine_only', mineOnly ? '1' : '0');
    }, [mineOnly]); // eslint-disable-line react-hooks/exhaustive-deps

    // Sync viewMode → URL
    useEffect(() => {
        setSearchParams(prev => {
            const p = new URLSearchParams(prev);
            if (p.has('experiment')) return p;
            if (viewMode === 'board') p.set('view', 'board'); else p.delete('view');
            return p;
        }, { replace: true });
    }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

    const projectOptions = Object.keys(PROJECT_MAP)
        .map(Number)
        .sort((a, b) => {
            if (a === 0) return 1;
            if (b === 0) return -1;
            return PROJECT_MAP[a].localeCompare(PROJECT_MAP[b]);
        });

    const clearFilters = () => {
        setQuery('');
        setProjectFilter('');
        setStateFilter('');
        setMineOnly(false);
    };

    const hasActiveFilters = query !== '' || projectFilter !== '' || stateFilter !== '' || mineOnly;

    const filtered = useMemo(() => {
        const q = query.toLowerCase().trim();
        return experiments.filter(exp => {
            if (projectFilter !== '' && String(exp.projectId) !== projectFilter) return false;
            if (stateFilter !== '' && exp.state !== stateFilter) return false;
            if (mineOnly && userEmail) {
                const ownerMatch = exp.owner?.toLowerCase() === userEmail.toLowerCase() ||
                    exp.email?.toLowerCase() === userEmail.toLowerCase();
                if (!ownerMatch) return false;
            }
            if (!q) return true;
            return (
                matches(exp.key, q) ||
                matches(exp.name, q) ||
                matches(exp.owner, q) ||
                matches(exp.email, q) ||
                matches(exp.state, q) ||
                matches(exp.experimentStateDisplayName, q) ||
                matches(getProjectName(exp.projectId), q)
            );
        });
    }, [experiments, query, projectFilter, stateFilter, mineOnly, userEmail]);

    return (
        <div>
            <AttentionBanner experiments={experiments} onSelect={onSelect} userEmail={userEmail} />

            <div className="field is-grouped is-grouped-multiline mb-3">
                <div className="control has-icons-left is-expanded">
                    <input
                        className="input"
                        type="text"
                        placeholder="Search by name, key, owner, or state…"
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        autoFocus
                    />
                    <span className="icon is-left">
                        <Icon icon={faSearch} />
                    </span>
                </div>
                <div className="control">
                    <div className="select">
                        <select value={projectFilter} onChange={e => setProjectFilter(e.target.value)}>
                            <option value="">All projects</option>
                            {projectOptions.map(id => (
                                <option key={id} value={String(id)}>{getProjectName(id)}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="control">
                    <div className="select">
                        <select value={stateFilter} onChange={e => setStateFilter(e.target.value)}>
                            {STATE_FILTER_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="control" style={{ display: 'flex', alignItems: 'center' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.875rem', userSelect: 'none' }}>
                        <input
                            type="checkbox"
                            checked={mineOnly}
                            onChange={e => setMineOnly(e.target.checked)}
                            style={{ cursor: 'pointer' }}
                        />
                        Mine only
                    </label>
                </div>
                <div className="control">
                    <div className="buttons has-addons" style={{ marginBottom: 0 }}>
                        <button
                            type="button"
                            className={`button ${viewMode === 'list' ? 'is-link is-selected' : 'is-light'}`}
                            onClick={() => setViewMode('list')}
                            title="List view"
                        >
                            <Icon icon={faTable} />
                        </button>
                        <button
                            type="button"
                            className={`button ${viewMode === 'board' ? 'is-link is-selected' : 'is-light'}`}
                            onClick={() => setViewMode('board')}
                            title="Board view — group by state"
                        >
                            <Icon icon={faThLarge} />
                        </button>
                    </div>
                </div>
            </div>

            {viewMode === 'board' ? (
                <KanbanBoard experiments={filtered} onSelect={onSelect} />
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table className="table is-fullwidth is-striped is-hoverable is-narrow" style={{ fontSize: '0.875rem' }}>
                        <thead>
                            <tr>
                                <th title="Experiment display name">Name</th>
                                <th title="Project this experiment belongs to">Project</th>
                                <th title="Unique experiment key used in code references">Key</th>
                                <th title="Person responsible for this experiment">Owner</th>
                                <th title="Current lifecycle phase (Design → Running → Ended → Finished)">State</th>
                                <th
                                    className="has-text-centered"
                                    title="Development environment running status — green dot = running, red dot = not running / paused / not started"
                                >
                                    Dev
                                </th>
                                <th
                                    className="has-text-centered"
                                    title="Production environment running status — green dot = running, red dot = not running / paused / not started"
                                >
                                    Prod
                                </th>
                                <th title="Last time this experiment was modified">Updated At</th>
                                <th title="When this experiment was created">Created At</th>
                                <th title="When this experiment started running in production">Running Since</th>
                            </tr>
                            <tr>
                                <td
                                    colSpan={10}
                                    style={{
                                        padding: '0.15rem 0.5rem 0.25rem',
                                        background: '#fafafa',
                                        borderBottom: '1px solid #ededed',
                                        fontSize: '0.72rem',
                                        color: '#888',
                                        textAlign: 'right',
                                    }}
                                >
                                    <span style={{ color: '#48c78e', marginRight: '0.2rem' }}>●</span> Running
                                    <span style={{ margin: '0 0.5rem' }}>·</span>
                                    <span style={{ color: '#f14668', marginRight: '0.2rem' }}>●</span> Not running / not started
                                </td>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={10} className="has-text-centered has-text-grey py-4">
                                        <p>No experiments match your filters.</p>
                                        {hasActiveFilters && (
                                            <button
                                                type="button"
                                                className="button is-small is-link is-light mt-2"
                                                onClick={clearFilters}
                                            >
                                                Clear filters
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ) : filtered.map(exp => (
                                <tr
                                    key={exp.key}
                                    onClick={() => onSelect(exp)}
                                    onKeyDown={e => e.key === 'Enter' && onSelect(exp)}
                                    tabIndex={0}
                                    role="button"
                                    aria-label={`Select experiment: ${exp.name ?? exp.key}`}
                                    style={{ cursor: 'pointer' }}>
                                    <td className="has-text-weight-semibold" style={{ maxWidth: '22ch', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={exp.name ?? ''}>{exp.name ?? '—'}</td>
                                    <td>{getProjectName(exp.projectId)}</td>
                                    <td className="has-text-grey is-family-monospace" style={{ fontSize: '0.8rem', maxWidth: '18ch', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={exp.key}>{exp.key}</td>
                                    <td>{exp.owner ?? exp.email ?? '—'}</td>
                                    <td><StateChip state={exp.state} /></td>
                                    <td className="has-text-centered"><StatusDot status={exp.devStatus} /></td>
                                    <td className="has-text-centered"><StatusDot status={exp.prodStatus} /></td>
                                    <td className="has-text-grey">{fmtDate(exp.updatedAt)}</td>
                                    <td className="has-text-grey">{fmtDate(exp.createdAt)}</td>
                                    <td className="has-text-grey">{fmtDate(exp.prodStartedAt)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <p className="has-text-grey is-size-7 mt-1">
                {filtered.length} experiment{filtered.length !== 1 ? 's' : ''}
                {query && ` matching "${query}"`}
            </p>
        </div>
    );
};

export default ExperimentSearch;
